package ru.education.building.schedule.widgets.calendar

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.SizeTransform
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.absoluteOffset
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowLeft
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.minimumInteractiveComponentSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.kizitonwose.calendar.compose.CalendarState
import com.kizitonwose.calendar.compose.HorizontalCalendar
import com.kizitonwose.calendar.compose.WeekCalendar
import com.kizitonwose.calendar.compose.weekcalendar.WeekCalendarState
import com.kizitonwose.calendar.core.DayPosition
import com.kizitonwose.calendar.core.WeekDayPosition
import com.kizitonwose.calendar.core.minusDays
import com.kizitonwose.calendar.core.plusDays
import kotlinx.coroutines.launch
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.YearMonth
import kotlinx.datetime.minus
import kotlinx.datetime.plus


val YearMonth.nextMonth: YearMonth get() = this.plus(1, DateTimeUnit.MONTH)
val YearMonth.previousMonth: YearMonth get() = this.minus(1, DateTimeUnit.MONTH)

@Composable
fun CustomCalendar(isWeekMode: Boolean, selectedDate: LocalDate, onSelectedChanged: (LocalDate) -> Unit, monthState: CalendarState, weekState: WeekCalendarState) {

    Column {
        CalendarTitle(isWeekMode = isWeekMode, monthState = monthState, weekState = weekState)
        AnimatedContent(
            targetState = isWeekMode,
            transitionSpec = {
                (fadeIn() togetherWith fadeOut())
                    .using(
                        SizeTransform(clip = false)
                    )
            },
        ) { targetMode ->
            if (targetMode) {
                WeekCalendar(
                    state = weekState,
                    dayContent = { day ->
                        Day(day.date, day.position == WeekDayPosition.RangeDate, selectedDate, onSelectedChanged)
                    },
                    weekHeader = {
                        DaysOfWeekTitle()
                    }
                )
            } else {
                HorizontalCalendar(
                    state = monthState,
                    dayContent = { day ->
                        Day(day.date, day.position == DayPosition.MonthDate, selectedDate, onSelectedChanged)
                    },
                    monthHeader = {
                        DaysOfWeekTitle()
                    }
                )
            }
        }
    }
}

@Composable
private fun CalendarTitle(isWeekMode: Boolean, monthState: CalendarState, weekState: WeekCalendarState) {
    //val visibleMonth = rememberFirstVisibleMonthAfterScroll(monthState, isAnimating)
    val visibleMonth = rememberVisibleWeekAndMonthAfterScroll(isWeekMode, weekState, monthState)
    val russianMonth = mapOf(
        "JANUARY" to "Январь", "FEBRUARY" to "Февраль", "MARCH" to "Март", "APRIL" to "Апрель", "MAY" to "Май",
        "JUNE" to "Июнь", "JULY" to "Июль", "AUGUST" to "Август", "SEPTEMBER" to "Сентябрь", "OCTOBER" to "Октябрь",
        "NOVEMBER" to "Ноябрь", "DECEMBER" to "Декабрь"
    )
    val coroutineScope = rememberCoroutineScope()

    Row(
        horizontalArrangement = Arrangement.SpaceBetween,
        modifier = Modifier
            .fillMaxWidth()
            .height(45.dp)
    ) {
        IconButton(
            onClick = {
                coroutineScope.launch {
                    if (isWeekMode) {
                        val targetDate = weekState.firstVisibleWeek.days.first().date.minusDays(1)
                        //fixVisibleMonth = targetDate.minusDays(6).month.toString()
                        weekState.animateScrollToWeek(targetDate)
                        //fixVisibleMonth = targetDate.minusDays(6).month.toString()
                    } else {
                        val targetMonth = monthState.firstVisibleMonth.yearMonth.previousMonth
                        //fixVisibleMonth = targetMonth.month.toString()
                        monthState.animateScrollToMonth(targetMonth)
                        //fixVisibleMonth = targetMonth.month.toString()
                    }
                }
            },
            modifier = Modifier
                .minimumInteractiveComponentSize() // 48.dp min
                .size(45.dp)
                .clip(CircleShape) // круговой риппл
        ) {
            Icon(
                imageVector = Icons.AutoMirrored.Filled.KeyboardArrowLeft,
                contentDescription = "goNext",
                modifier = Modifier.absoluteOffset(x = (-1).dp)
            )
        }
        //val middleDate = visibleWeek.days.first().date.daysUntil(visibleWeek.days.last().date) / 2
        //visibleWeek.days.first().date.plus(middleDate, DateTimeUnit.DAY).month.toString()
        //visibleWeek.days.first().date.plus(middleDate, DateTimeUnit.DAY).month.toString()
        Text(
            text = russianMonth[visibleMonth] ?: visibleMonth,
            textAlign = TextAlign.Center,
            fontSize = 16.sp,
            fontWeight = FontWeight.W500,
            modifier = Modifier
                .height(45.dp)
                .wrapContentHeight()
        )
        IconButton(
            onClick = {
                coroutineScope.launch {
                    if (isWeekMode) {
                        val targetDate = weekState.firstVisibleWeek.days.last().date.plusDays(1)
                        //fixVisibleMonth = targetDate.month.toString()
                        weekState.animateScrollToWeek(targetDate)
                        //fixVisibleMonth = targetDate.month.toString()
                    } else {
                        val targetMonth = monthState.firstVisibleMonth.yearMonth.nextMonth
                        //fixVisibleMonth = targetMonth.month.toString()
                        monthState.animateScrollToMonth(targetMonth)
                        //fixVisibleMonth = targetMonth.month.toString()
                    }
                }
            },
            modifier = Modifier
                .minimumInteractiveComponentSize() // 48.dp min
                .size(45.dp)
                .clip(CircleShape) // круговой риппл
        ) {
            Icon(
                imageVector = Icons.AutoMirrored.Filled.KeyboardArrowRight,
                contentDescription = "goBack",
            )
        }
    }
}

@Composable
private fun DaysOfWeekTitle() {
    val russianDays = listOf("Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс")

    Row(modifier = Modifier.fillMaxWidth()) {
        for (dayOfWeek in russianDays) {
            Text(
                text = dayOfWeek,
                textAlign = TextAlign.Center,
                fontSize = 12.sp,
                fontWeight = FontWeight.W500,
                color = Color.Gray,
                modifier = Modifier.weight(1f)
            )
        }
    }
}

@OptIn(ExperimentalFoundationApi::class)
@Composable
private fun Day(day: LocalDate, isSelected: Boolean, selectedDate: LocalDate, onSelectedChanged: (LocalDate) -> Unit) {

    val interactionSource = remember { MutableInteractionSource() }

    Box(
        contentAlignment = Alignment.Center,
        modifier = Modifier
            .aspectRatio(1.1f)
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                enabled = isSelected,
                onClick = {
                    onSelectedChanged(day)
                }
            )
    ) {
        // возможно убрать box
        Box(
            contentAlignment = Alignment.Center,
            modifier = Modifier
                .background(
                    color = if (selectedDate == day) MaterialTheme.colorScheme.primary else Color.Transparent,
                    shape = CircleShape
                )
                .width(35.dp)
                //.fillMaxWidth(0.65f)
                .aspectRatio(1f)
        ) {
            // wrapContentSize(Alignment.Center) - попробовать
            Text(
                text = day.day.toString(),
                fontSize = 14.sp,
                fontWeight = FontWeight.W600,
                color = if (selectedDate == day) Color.White else if (!isSelected) Color.Gray else Color.Unspecified
            )
        }
    }
}

